import { IGlobalAllocationsBindingModel } from "./dashboard/global-map/types"
import { IHealthySoilsAssessment, IHsaDrainage, IHsaDrillType, IHsaPrimaryCultivations, IHsaSoilSeries } from "./hsa/types"
import { INitrogenAnalysis } from "./nitrogen/types"
import { FieldViewModel, IComponentContext, IComponentStartOptions, IDatalayerViewerFactory, IGenericAnalysis, ILaboratory, IModule, IOmniaService, IOmniaSettings, IPcnAnalysis, ISoilAnalysis, ISoilAnalysisColor, ISoilUnitService, OmniaAppId, OrderInvoice, SharedFieldsService, shell, soil, SoilAnalysisType, SoilUnit, SsmClassification } from "./omnia"
import { IDataLayer, IDataLayerValue, IDataLayerRange, IDataLayerZone, IDataLayerType, ILayerViewModel, IDatalayerZoneMapInstance, LayerTypeId, NitrogenAnalysisType, isMapLayerZone } from "./omnia"

export type { SharedFieldsService, IDataLayer, IDataLayerValue, IDataLayerRange, IDataLayerZone, IDataLayerType, ILayerViewModel }

export type IFieldsService = SharedFieldsService

export type LayerZone = IDatalayerZoneMapInstance

export { LayerTypeId, isMapLayerZone, NitrogenAnalysisType }

export interface ISoilAnalysisAllocBindingModel {

    NewAllocations: {

        FieldId: number
        AnalysisId: number

    }[]

    RemovedAllocations: number[]

}

export interface IPcnAnalysisAllocBindingModel extends ISoilAnalysisAllocBindingModel {
}


export interface INitrogenAnalysisBindingModel {

    NitrogenAnalysis: INitrogenAnalysis

    Allocations?: {
        CroppingId: number
        FieldId: number
        CropN?: number
        SMN?: number
    }[]

}


export interface INitrogenAnalysisAllocBindingModel {

    Allocations: {

        NitrogenAnalysisAllocId: number

        NitrogenAnalysisId: number
        CroppingId: number

        CropN?: number
        OrganicMatter?: number

    }[]

    RemovedAllocations: number[]

}

export interface PropertyInterpretation {
    PropertyName: string
    Comment: string
}

export interface ISoilService extends IOmniaService {

    readonly settings: IOmniaSettings
    readonly units: ISoilUnitService

    readonly fieldSvc: SharedFieldsService
    readonly fields: FieldViewModel[]
    readonly activeFields: FieldViewModel[]
    readonly cropping: FieldViewModel[]

    getLaboratories(): ILaboratory[]

    getSoilAnalyses(type?: SoilAnalysisType, terramap?: boolean): ISoilAnalysis[]
    hasSsmAnalyses(): boolean
    getNitrogenAnalysis(includeFarmAssesments?: boolean): INitrogenAnalysis[]
    getHealthySoilsAssessments(): IHealthySoilsAssessment[]
    getSnsAssessments(): ISnsAssessment[]
    getPcnAnalysis(): IPcnAnalysis[]

    getHealthySoilsAssessmentSeries(): IHsaSoilSeries[]
    getHsaDrainage(): IHsaDrainage[]
    getPrimaryCultivations(): IHsaPrimaryCultivations[]
    getDrillTypes(): IHsaDrillType[]
    getInterpretations(): IInterpretationGroup[]

    getSsmClassification(): SsmClassification[]

    getAnalysisOrderInProgress(): boolean

    saveSoilAnalysis(soilAnalysis: ISoilAnalysis, fieldsIds: number[], comments: PropertyInterpretation[]): ng.IPromise<ISoilAnalysis>
    updateSoilAnalysisAlloc(model: ISoilAnalysisAllocBindingModel): ng.IPromise<any>
    deleteSoilAnalysis(id: number, deleteOtherGridSamples: boolean)

    saveNitrogenAnalysis(model: INitrogenAnalysisBindingModel): ng.IPromise<number>
    updateNitrogenAnalysisAlloc(model: INitrogenAnalysisAllocBindingModel): ng.IPromise<any>
    deleteNitrogenAnalysis(id: number)

    generateSoilAnalysis(fields: IGenerationInput[]): ng.IPromise<any>

    savePcnAnalysis(analysis: IPcnAnalysis, fieldsIds: number[]): ng.IPromise<number>
    deletePcnAnalysis(id: number, deleteOtherGridSamples: boolean): ng.IPromise<any>
    updatePcnAnalysisAlloc(pcnAnalysisAllocBindingModel: IPcnAnalysisAllocBindingModel): ng.IPromise<any>

    deleteHsa(hsaId: number): ng.IPromise<any>

    saveGlobalAnalysisAlloc(model: IGlobalAllocationsBindingModel): ng.IPromise<any>

    findField(fieldId: number, includeDisabled?: boolean): FieldViewModel
    findCropping(croppingId: number): FieldViewModel

    getFieldSoilAnalyses(fieldId: number, zones?: boolean): ISoilAnalysis[]
    getFieldPcnAnalysis(fieldId: number): IPcnAnalysis[]

    getLayerTypes(): IDataLayerType[]
    getLayerType(typeId: LayerTypeId): IDataLayerType

    getSoilLayers(loadZones: boolean, types: LayerTypeId[]): ng.IPromise<IDataLayer[]>
    getSoilAnalysisLayers(loadZones: boolean): ng.IPromise<IDataLayer[]>
    getSoilTypeLayers(loadZones: boolean): ng.IPromise<IDataLayer[]>

    unallocSoilAnalysis(soilAnalysisId: number, fieldId: number): ng.IPromise<any>

    unallocNitrogenAnalysis(nitrogenAnalysisId: number, croppingId: number): ng.IPromise<any>

    unallocPcnAnalysis(pcnAnalysisId: number, fieldId: number): ng.IPromise<any>

    getAnalysisOrderInProgress(): boolean
    orderSoilAnalysis(order: ISoilAnalysisOrder, selection: any): ng.IPromise<any>

    saveHsa(healthySoilsAssessment: IHealthySoilsAssessment, fieldIds: number[], location: number[]): ng.IPromise<any>
    allocHsa(fieldId: number, assessmentIds: number[]): ng.IPromise<any>
    unallocHsa(healthySoilAssessmentId: number, fieldId: number): ng.IPromise<any>

}

export interface IGenerationInput {
    fieldId: number
    datalayerId: number
}

export interface ISoilModuleContext {

    svc: ISoilService
    module: IModule

    getCurrentContext(): IComponentContext
    startComponent(options: IComponentStartOptions): IComponentContext

}

export enum AnalysisType {
    Soil = 1,
    Nitrogen = 2
}

export interface IAnalysisEditCommand {
    analysis: IGenericAnalysis
    edit: boolean
    context: IAnalysisEditorContext
    alloc?: FieldViewModel
}

export interface IAnalysisEditorContext {
    refresh(updatedTargetId: number)
}

export interface ISoilAnalysisOrder {
    Brand: OmniaAppId
    HoldingId: number
    YearId: number
    UserId: number
    SamplingCode: string
    AdditionalAnalysis: string
    Invoicing: OrderInvoice
    InvoicingInstructions: string
}

export interface IAnalysisValueColumn {

    visible?: boolean

    name: string
    units?: string
    header?: string

    gauge?: boolean

    /** True if values can be displayed as indices */
    index?: boolean

    /** True if values can be converted to/from Olsen/Morgan */
    morgan?: boolean

    selectable?: boolean

    key?: string
    alloc?: boolean

    formatter?: (value: any) => string
}

export interface IInterpretation {
    InterpretationId: number
    PropertyName: string
    Min: number
    Comment: string
}

export interface IInterpretationGroup {
    Name: string
    Index: number
    Properties: IInterpretationProperty[]
}

export interface IInterpretationProperty {
    Label: string
    PropertyName: string
    Unit?: string
    Values: IInterpretation[]
}

export interface ISnsAssessment {
    FieldId: number
    CroppingId: number
    SnsValue: number
    SnsIndex: number
    CalcDate: Date
}

export interface ISoilAnalysisLayerViewerFactory extends IDatalayerViewerFactory {

    colors?: ISoilAnalysisColor[]
    units?: string | (() => SoilUnit)

}
